import { useCallback } from "react";

import type { ModelViewerElement } from "@google/model-viewer/lib/model-viewer";
import { Button, ButtonIconWithLabel, Icon_Download24, isLoggedIn } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import { defaultPrivateSpaceName, defaultTeamSpaceName } from "../../../config";
import useAccount from "../../../hooks/useAccount";
import { useProject } from "../../../hooks/useProject";
import { useProjectViewLinkSharing } from "../../../hooks/useProjectViewLink";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { useWebExport } from "../../../hooks/useWebExport";
import { ElementClickAction } from "../../../lib/TrackingAdapter";
import { ProjectRoleUtils } from "../../../types";
import { FloatingBreadcrumbs } from "../../ProjectSidebar/FloatingBreadcrumbs/FloatingBreadcrumbs";
import { LoginButton } from "../components/LoginButton/LoginButton";
import { MenuHeader } from "../components/MenuHeader/MenuHeader";
import QRCodeModal from "../components/QRCodeModal/QRCodeModal";
import { ShareProjectModal } from "../components/ShareModal/ShareProjectModal";
import { ShareViewLinkModal } from "../components/ShareModal/ShareViewLinkModal";

import style from "./TopAppBarFloating.module.css";

interface TopAppBarFloatingProps {
  disableARButton?: boolean;
  hideARButton?: boolean;
  isDrawingsLayout?: boolean;
  modelViewer?: React.RefObject<ModelViewerElement | null>;
  downloadPdf?: (drawingId: string) => void;
  centerMessage?: string | React.ReactNode;
  isOpen?: boolean;
}

const spaceTypeNameMap: Record<string, string> = {
  private: defaultPrivateSpaceName,
  team: defaultTeamSpaceName,
};

const TopAppBarFloating: React.FC<TopAppBarFloatingProps> = ({
  disableARButton,
  hideARButton,
  isDrawingsLayout = false,
  modelViewer,
  downloadPdf = () => null,
  centerMessage,
  isOpen = true,
}) => {
  const tracker = useTracker();
  const { t, m } = useTranslation();

  const { drawingId } = useParams();
  const { data: accountData, isPending: isAccountPending } = useAccount();
  const { data: projectData } = useProject();
  const { data: projectViewLinkSharingData } = useProjectViewLinkSharing();
  const { data: webExportData, viewLinkId } = useWebExport();

  const onDownloadPdfButtonclick = useCallback(() => {
    tracker.trackButtonClick({
      action: ElementClickAction.DOWNLOAD_PDF,
      elementId: "download-pdf-button",
    });

    if (drawingId && isDrawingsLayout) {
      downloadPdf(drawingId);
    }
  }, [drawingId, downloadPdf, isDrawingsLayout, tracker]);

  const spaceName =
    projectData?.space?.spaceName ??
    spaceTypeNameMap[projectData?.space?.spaceType ?? ""] ??
    undefined;

  return (
    <div className={style.menuFloatingWrapper}>
      <div className={style.menuLeftFloating}>
        {projectData?.teamId && spaceName && (
          <FloatingBreadcrumbs teamId={projectData?.teamId} spaceName={spaceName} isOpen={isOpen} />
        )}
      </div>
      <div className={style.menuCenterFloating}>
        {centerMessage && <div className={style.centerWrapper}>{centerMessage}</div>}
      </div>
      <div className={style.menuRightFloating}>
        <div className={style.userMenu}>
          {!hideARButton && (
            <>
              {drawingId && isDrawingsLayout && (
                <Button
                  className={style.pdfButton}
                  variant="borderless"
                  onClick={onDownloadPdfButtonclick}
                  onMouseDown={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <ButtonIconWithLabel
                    icon={<Icon_Download24 />}
                    label={t(m.ModelViewer.PdfButtonLabel)}
                  />
                </Button>
              )}

              {!(drawingId && isDrawingsLayout) && (
                <QRCodeModal
                  className={style.arButton}
                  modelViewer={modelViewer}
                  disabled={disableARButton}
                  variant="borderless"
                  url={`${window.location.origin}/p/${webExportData?.webExport.projectId}/v/${viewLinkId}`}
                />
              )}
            </>
          )}
          {projectData && ProjectRoleUtils.isAtLeast(projectData.userRole, "editor") && (
            <ShareProjectModal />
          )}
          {projectViewLinkSharingData && <ShareViewLinkModal hideButton />}
          {isLoggedIn() && !isAccountPending && (
            <div className={style.menuHeaderWrapper}>
              <MenuHeader accountData={accountData} isCompact />
            </div>
          )}
          {!isLoggedIn() && (
            <>
              <div className={style.separator} />
              <LoginButton variant="soft" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopAppBarFloating;
